import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    inject,
} from '@angular/core';
import { JsonPipe, NgTemplateOutlet } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import moment, { Moment } from 'moment';
import { Subscription, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { confirm } from 'devextreme/ui/dialog';
import type { Properties as ButtonProperties } from 'devextreme/ui/button';
import { DxButtonComponent } from 'devextreme-angular/ui/button';
import { DxCheckBoxComponent } from 'devextreme-angular/ui/check-box';
import { DxDataGridComponent } from 'devextreme-angular/ui/data-grid';
import { DxNumberBoxComponent } from 'devextreme-angular/ui/number-box';
import { DxPopupComponent } from 'devextreme-angular/ui/popup';
import { DxLoadIndicatorComponent } from 'devextreme-angular/ui/load-indicator';
import {
    DxTextBoxComponent,
    DxiTextBoxButtonComponent,
} from 'devextreme-angular/ui/text-box';
import {
    DxiColumnComponent,
    DxoFilterRowComponent,
    DxoHeaderFilterComponent,
    DxoMasterDetailComponent,
    DxoPagerComponent,
    DxoPagingComponent,
} from 'devextreme-angular/ui/nested';
import { DxTemplateDirective } from 'devextreme-angular/core';
import {
    CancelledOrder,
    CancelledOrderLookupService,
    HeaderContentService,
    MessageType,
    NotificationService,
    PACKAGE_EXPECTED_STAGES,
    PACKAGE_SOURCE_LABELS,
    PACKAGE_STAGE_LABELS,
    ReturnFetchReport,
    ReturnOrdersService,
    ReturnPackage,
    ReturnReceiptService,
    ReturnReceiptsDoc,
    UtilService,
    awaitingSellerDecision,
    codeReceiptKey,
    looksLikeCourierLabel,
    matchesCode,
    normalizeCode,
    receiptFor,
    returnSearchKeys,
} from 'src/app/shared/services';
import { STORES } from 'src/app/shared/config/store-config';
import { ProductCardPopupComponent } from 'src/app/shared/components/product-card-popup/product-card-popup.component';
import { ReturnDetailPopupComponent } from 'src/app/shared/components/return-detail-popup/return-detail-popup.component';
import { FormatRelativeDatetimePipe } from 'src/app/shared/pipes/format-relative-datetime.pipe';
import { ThaiDateOrRelativePipe } from 'src/app/shared/pipes/thai-datetime.pipe';
import { FormatNumberPipe } from 'src/app/shared/pipes/format-number.pipe';

// A return as the grid shows it: the normalized platform record plus what
// this shop has recorded about it and a few flattened fields, so columns,
// header filters and sorting work on plain values.
interface ReturnRow extends ReturnPackage {
    // คืนสินค้า / ตีกลับ — see PackageSource.
    sourceLabel: string;
    // What the Package column shows: the shop's own confirmation once it
    // exists, else the platform's stage. A confirmed package is at the shop
    // whatever the carrier last said, and that is the fact the operator
    // needs at a glance.
    stageLabel: string;
    // The platform's stage regardless of confirmation, kept for the hover.
    platformStageLabel: string;
    itemsSummary: string;
    createdAtDate: Date | null;
    received: boolean;
    receivedAt: string | null;
    receivedBy: string;
}

const DEFAULT_LOOKBACK_DAYS = 60;

// A barcode glyph for the scan box: DevExtreme's icon set has none. Scan
// corners plus bars of uneven weight, light grey to sit quietly on the dark
// theme. An image URL is what a text-box button's `icon` accepts.
const BARCODE_ICON =
    'data:image/svg+xml;utf8,' +
    encodeURIComponent(
        `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='#b4b4b4' stroke-linecap='round'>` +
            `<path stroke-width='1.6' d='M3 8V4h4M21 8V4h-4M3 16v4h4M21 16v4h-4'/>` +
            `<path stroke-width='1.3' d='M7 8v8M12 8v8M17 8v8'/>` +
            `<path stroke-width='2.3' d='M9.4 8v8M14.6 8v8'/>` +
            `</svg>`,
    );

// localStorage key for the grid's date display (relative / absolute).
const DATE_MODE_STORAGE_KEY = 'return-packages-date-mode';

const readAbsoluteDateMode = (): boolean => {
    try {
        return localStorage.getItem(DATE_MODE_STORAGE_KEY) === 'absolute';
    } catch {
        return false;
    }
};

// What an Enter in the scan box found — see onSearchEnter.
type ScanResult =
    | { kind: 'match'; row: ReturnRow }
    | { kind: 'received'; row: ReturnRow }
    | { kind: 'multi'; rows: ReturnRow[] }
    // Still working it out: waiting for the stores to finish loading,
    // refreshing the data after a miss, or checking the cancelled orders.
    | { kind: 'pending'; stage: 'loading' | 'refresh' | 'cancelled' }
    | { kind: 'cancelled'; order: CancelledOrder }
    // An order number the platform knows that is not in the table: a failed
    // delivery from before the lookback (`failedDelivery`), or a live order
    // with no return — a parcel back on the counter with no paperwork yet.
    | {
          kind: 'order';
          order: {
              storeTitle: string;
              orderNo: string;
              status: string;
              failedDelivery: boolean;
          };
          failed: string[];
      }
    | { kind: 'none'; failed: string[] };

// Shown in the Package column in place of the platform's stage once the
// shop has confirmed the package arrived.
const RECEIVED_STAGE_LABEL = 'ยืนยันรับแล้ว';

// How often the open page re-reads the index (plus the live top-up).
const REFRESH_EVERY_MS = 10 * 60 * 1000;
// A scan that finds nothing refreshes the data first — but not more often
// than this, so a run of unknown labels does not hammer the platforms.
const MISS_REFRESH_MIN_MS = 60 * 1000;

// How long a scan result must have been on screen before an Enter acts on
// it. A scanner's trailing Enter (and a CR+LF scanner's second one) lands
// within milliseconds; a person reading the popup takes far longer.
const SCAN_ENTER_GUARD_MS = 400;

@Component({
    selector: 'app-return-packages',
    templateUrl: './return-packages.component.html',
    styleUrls: ['./return-packages.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        JsonPipe,
        NgTemplateOutlet,
        DxPopupComponent,
        DxLoadIndicatorComponent,
        DxButtonComponent,
        DxCheckBoxComponent,
        DxDataGridComponent,
        DxNumberBoxComponent,
        DxTextBoxComponent,
        DxiTextBoxButtonComponent,
        DxiColumnComponent,
        DxoFilterRowComponent,
        DxoHeaderFilterComponent,
        DxoMasterDetailComponent,
        DxoPagerComponent,
        DxoPagingComponent,
        DxTemplateDirective,
        ProductCardPopupComponent,
        ReturnDetailPopupComponent,
        FormatRelativeDatetimePipe,
        ThaiDateOrRelativePipe,
        FormatNumberPipe,
    ],
})
export class ReturnPackagesComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    private cdref = inject(ChangeDetectorRef);
    private headerContentService = inject(HeaderContentService);
    private destroyRef = inject(DestroyRef);
    private notificationService = inject(NotificationService);
    private returnOrdersService = inject(ReturnOrdersService);
    private returnReceiptService = inject(ReturnReceiptService);
    private utilService = inject(UtilService);
    private router = inject(Router);
    private route = inject(ActivatedRoute);
    private cancelledOrderLookup = inject(CancelledOrderLookupService);

    @ViewChild('searchBox', { static: true })
    private searchBox!: DxTextBoxComponent;

    @ViewChild('productCard', { static: true })
    private productCard!: ProductCardPopupComponent;

    @ViewChild('detailPopup', { static: true })
    private detailPopup!: ReturnDetailPopupComponent;

    // The page title, projected into the app header's centre slot.
    @ViewChild('headerMenu', { static: true })
    private headerMenu!: TemplateRef<unknown>;

    lookbackDays = DEFAULT_LOOKBACK_DAYS;
    loading = false;
    // A silent refresh in flight (the index re-read plus the live top-up —
    // see refresh()). Unlike `loading` it neither clears the table nor holds
    // a scan back.
    refreshing = false;
    private lastRefreshAt = 0;
    private afterRefresh: (() => void)[] = [];
    // The rows fetch in flight (a load or a refresh): a load cancels a
    // refresh still running, so its stores cannot land over the new ones.
    private rowsSub?: Subscription;
    // The code a miss already refreshed the data for: a second miss on it
    // goes straight to the cancelled-order check.
    private refreshedForCode = '';
    private refreshTimer?: ReturnType<typeof setInterval>;
    // Stores whose fetch problems were already announced this load: a store
    // arrives twice from the index (then its top-up), and says it once.
    private announced = new Set<string>();
    // Rows per store: each arrival of a store replaces what it had.
    private storeRows = new Map<string, ReturnPackage[]>();
    // Every return fetched, minus requests still awaiting the shop's
    // decision (they may yet end as a refund with nothing shipped back).
    private fetched: ReturnPackage[] = [];
    // The ones the page tracks: a package is (or was) coming back — or the
    // shop has already confirmed one arrived, which outranks a platform
    // verdict of "no package" (a TikTok case closed without tracking, say):
    // a confirmed parcel must never drop off the tracker or the inspection
    // page. Rebuilt with the receipts in rebuild().
    private rows: ReturnPackage[] = [];
    reports: ReturnFetchReport[] = [];
    // The confirmations on record. Marks are refused until the first
    // successful fetch: a save is built from the stored document, but the
    // page must not present a stale (empty) view as the truth meanwhile.
    private receipts: ReturnReceiptsDoc = { receipts: {} };
    receiptsLoaded = false;
    receiptSaving = false;
    // What the operator typed or scanned. While non-empty the grid shows only
    // the returns it identifies, received or not.
    searchCode = '';
    hideReceived = false;
    dataSource: ReturnRow[] = [];
    // The returns the current search identifies, whatever the other filters
    // say — what an Enter in the scan box reports on.
    matches: ReturnRow[] = [];
    // What the last Enter found, shown in the scan-result popup. The
    // scanner's own Enter only ever OPENS a result; a further Enter on the
    // same code, once the result has been on screen a moment, takes its
    // default action (see onOpenResultEnter). Focus stays in the scan box
    // the whole time (the popup takes none), so the next scan always lands
    // in the box, never on a popup button.
    scanResult: ScanResult | null = null;
    scanPopupVisible = false;
    // The code the open popup belongs to, so a cancelled-order check that
    // finishes after the box has changed does not fill a stale popup.
    private scanCode = '';
    // When the current result first appeared (Date.now()), and what it was,
    // so a re-judged identical result keeps its time — see
    // onOpenResultEnter.
    private scanShownAt = 0;
    private scanShownKey = '';
    // A scan whose Enter arrived while the open result could not yet give
    // way — a save in flight, or a found parcel still waiting for ยืนยัน /
    // ยกเลิก — answered once that settles (see onSearchEnter).
    queuedEnter = false;
    // The code the open result belongs to, as scanned — the popup's title
    // and the code a confirmation records, even while the next code is
    // already being typed into the box.
    scanShownCode = '';
    // An attempt to leave a found parcel undecided (Escape, the next scan):
    // the popup says what it is waiting for.
    decisionNudge = false;
    // The code of the scan result being saved; '' for a save started from a
    // grid row. An Enter on it during the save is impatience, not a scan.
    private savingCode = '';
    // The barcode glyph at the start of the scan box. Decorative: it takes
    // no focus and a click on it just focuses the box.
    readonly scanIconButton: ButtonProperties = {
        icon: BARCODE_ICON,
        stylingMode: 'text',
        hint: 'สแกนบาร์โค้ดหรือพิมพ์รหัส',
        focusStateEnabled: false,
        hoverStateEnabled: false,
        activeStateEnabled: false,
        tabIndex: -1,
        elementAttr: { class: 'scan-icon-button' },
        onClick: () => this.focusSearch(),
    };

    // Dates in the grid: relative ("3 วันที่แล้ว") or the real date. One
    // switch for the whole table, flipped by clicking any date, and
    // remembered per browser.
    showAbsoluteDates = readAbsoluteDateMode();

    ngOnInit(): void {
        // `?code=` pre-fills the scan box: the orders page links here with an
        // order number so the request it flagged is the one on screen.
        const code = this.route.snapshot.queryParamMap.get('code');
        if (code) {
            this.searchCode = code;
        }
        this.load();
        // The page stays open at the packing counter all day; the index
        // moves every 15 minutes, so the table follows it. Not when the
        // table was swept live (the index unusable, or a lookback past it):
        // that refresh would be a full sweep of every store.
        this.refreshTimer = setInterval(() => {
            if (document.visibilityState === 'visible' && this.fromIndex) {
                this.refresh();
            }
        }, REFRESH_EVERY_MS);
    }

    ngAfterViewInit(): void {
        this.headerContentService.set(this.headerMenu);
        // Ready to scan the moment the page opens.
        this.focusSearch();
    }

    ngOnDestroy(): void {
        this.headerContentService.clear(this.headerMenu);
        clearInterval(this.refreshTimer);
    }

    readonly dateToggleHint = ' · คลิกเพื่อสลับรูปแบบวันที่';

    toggleDateMode(event: Event): void {
        // A date click is not a row click.
        event.stopPropagation();
        this.showAbsoluteDates = !this.showAbsoluteDates;
        try {
            localStorage.setItem(
                DATE_MODE_STORAGE_KEY,
                this.showAbsoluteDates ? 'absolute' : 'relative',
            );
        } catch {
            // Storage blocked: the switch still works for this visit.
        }
        this.cdref.markForCheck();
    }

    // The product card, opened from a SKU in the grid, the detail panel or
    // the detail popup.
    openProduct(sku: string): void {
        this.productCard.open(sku);
    }

    // The detail popup, opened from the type badge. The scan box keeps focus
    // (the popup takes none), so a parcel scanned meanwhile still lands in
    // the box — and its result replaces this popup (showScanResult).
    openDetail(row: ReturnRow, event: Event): void {
        // A badge click is not a row click.
        event.stopPropagation();
        this.detailPopup.open(row, receiptFor(row, this.receipts));
        this.focusSearch();
    }

    onDetailHidden(): void {
        this.refocusUnlessTyping();
    }

    // The inspection report is reached from here rather than the side menu:
    // it is the next step for a package once it is confirmed received.
    openInspection(): void {
        this.router.navigate(['/secure/orders/returns/inspection']);
    }

    // Fetches the confirmations and the returns from scratch. From the
    // collector's index the whole table arrives at once; swept live, the
    // stores arrive one at a time and the grid fills as they do. A failed
    // confirmations fetch leaves the page read-only (receiptsLoaded stays
    // false) rather than showing every package as unconfirmed.
    load(): void {
        if (this.loading) {
            return;
        }
        // A refresh still running is superseded; what waits on it waits on
        // this load instead (finishLoad drains afterRefresh).
        this.rowsSub?.unsubscribe();
        this.refreshing = false;
        this.loading = true;
        this.announced.clear();
        this.storeRows.clear();
        this.fetched = [];
        this.reports = [];
        this.rebuild();
        this.fetchReceipts(false);
        this.fetchRows(true, () => this.finishLoad());
    }

    // Re-reads the confirmations (other operators' too) and the returns
    // without clearing anything: the table keeps what it shows while each
    // store's fresher rows replace its old ones. `done` runs once it lands.
    private refresh(done?: () => void): void {
        if (done) {
            this.afterRefresh.push(done);
        }
        if (this.loading || this.refreshing) {
            return;
        }
        this.refreshing = true;
        this.lastRefreshAt = Date.now();
        this.fetchReceipts(true);
        this.fetchRows(false, () => {
            this.refreshing = false;
            this.drainAfterRefresh();
            this.cdref.markForCheck();
        });
    }

    private drainAfterRefresh(): void {
        const callbacks = this.afterRefresh;
        this.afterRefresh = [];
        callbacks.forEach((callback) => callback());
    }

    private fetchReceipts(silent: boolean): void {
        this.returnReceiptService
            .getDoc()
            .pipe(
                catchError((error: unknown) => {
                    if (!silent) {
                        this.notificationService.pushMessage({
                            message: `โหลดข้อมูลการรับไม่ได้: ${this.utilService.errorToString(error)}`,
                            messageType: MessageType.error,
                        });
                    }
                    return of(null);
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((receipts) => {
                // Not while a save is in flight: its answer carries the
                // newer document.
                if (receipts && !this.receiptSaving) {
                    this.receipts = receipts;
                    this.receiptsLoaded = true;
                }
                this.rebuild();
                // An open result may have been judged before the receipts
                // were known (e.g. 'match' for a parcel already received).
                this.refreshOpenScan();
            });
    }

    // `progressive`: a first load, which may show each store's index rows
    // before its live top-up lands; a refresh gets each store once.
    private fetchRows(progressive: boolean, onComplete: () => void): void {
        this.rowsSub = this.returnOrdersService
            .load(this.lookbackDays, { progressive })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (result) => {
                    const storeId = result.report.storeId;
                    this.storeRows.set(
                        storeId,
                        result.rows.filter(
                            (row) => !awaitingSellerDecision(row),
                        ),
                    );
                    this.fetched = [...this.storeRows.values()].flat();
                    this.reports = [
                        ...this.reports.filter((r) => r.storeId !== storeId),
                        result.report,
                    ];
                    this.rebuild();
                    if (!this.announced.has(storeId)) {
                        this.announced.add(storeId);
                        this.reportFetchProblem(result.report);
                    }
                    this.refreshOpenScan();
                },
                error: (error: unknown) => {
                    this.notificationService.pushMessage({
                        message: this.utilService.errorToString(error),
                        messageType: MessageType.error,
                    });
                    onComplete();
                },
                // Not a finalize(): that also runs when the page is
                // destroyed, and would then evaluate a scan on a dead view.
                complete: () => onComplete(),
            });
    }

    // When the table was last collected, if it came from the index: the
    // oldest store's time, since that is what the whole table is as of.
    get indexedAt(): Moment | null {
        if (
            this.reports.length === 0 ||
            this.reports.some((report) => !report.indexedAt)
        ) {
            return null;
        }
        return moment.min(this.reports.map((report) => report.indexedAt!));
    }

    // Whether the table on screen came from the index (a refresh is then
    // one cheap call) rather than a live sweep of every store.
    private get fromIndex(): boolean {
        return this.indexedAt !== null;
    }

    private finishLoad(): void {
        this.loading = false;
        this.lastRefreshAt = Date.now();
        this.drainAfterRefresh();
        this.refreshOpenScan();
        this.cdref.markForCheck();
    }

    get storeCount(): number {
        return STORES.length;
    }

    get failedReports(): ReturnFetchReport[] {
        return this.reports.filter((report) => !!report.error);
    }

    // Stores whose returns loaded but whose failed deliveries did not.
    get failedDeliveryReports(): ReturnFetchReport[] {
        return this.reports.filter((report) => !!report.failedDeliveryError);
    }

    // The summary line in full, for the hover title once it is clipped.
    get summaryText(): string {
        const parts = [
            `${this.fetchedCount} พัสดุคืน/ตีกลับใน ${this.lookbackDays} วันล่าสุด`,
            `${this.unreceivedExpectedCount} พัสดุที่ยังไม่ยืนยันรับ`,
        ];
        if (this.loading) {
            parts.unshift(
                `กำลังดึงข้อมูล ${this.reports.length}/${this.storeCount} ร้าน`,
            );
        }
        if (!this.receiptsLoaded) {
            parts.push('ยังโหลดข้อมูลการรับไม่ได้ — ดูได้อย่างเดียว');
        }
        for (const report of this.failedReports) {
            parts.push(`${report.title}: ${report.error}`);
        }
        for (const report of this.failedDeliveryReports) {
            parts.push(
                `${report.title}: โหลดพัสดุตีกลับไม่ได้ — ${report.failedDeliveryError}`,
            );
        }
        if (this.detailFailureCount > 0) {
            parts.push(
                `${this.detailFailureCount} รายการ Shopee ไม่มีสถานะขนส่ง`,
            );
        }
        return parts.join(' · ');
    }

    get detailFailureCount(): number {
        return this.reports.reduce(
            (sum, report) => sum + (report.detailFailures ?? 0),
            0,
        );
    }

    get fetchedCount(): number {
        return this.rows.length;
    }

    get unreceivedExpectedCount(): number {
        return this.rows.filter((row) => !receiptFor(row, this.receipts))
            .length;
    }

    get hasSearch(): boolean {
        return normalizeCode(this.searchCode).length > 0;
    }

    // Nothing matches yet, but stores are still loading: not yet a miss.
    get awaitingLoad(): boolean {
        return this.matches.length === 0 && this.loading;
    }

    // The scan that found nothing reads like a courier label. When a courier
    // cannot deliver a parcel it sends it back under a return label of its
    // own (SPXTH…, LEXPU…), which no marketplace records — the original
    // label underneath still carries the order number that finds it.
    get scannedCourierLabel(): boolean {
        return looksLikeCourierLabel(this.scanCode);
    }

    // "Record anyway" is offered only once the scan has been checked against
    // the cancelled orders and found not to be one — otherwise a cancelled
    // parcel would get recorded as a return and its stock counted twice.
    get canRecordUnmatched(): boolean {
        return (
            this.hasSearch &&
            (this.scanResult?.kind === 'none' ||
                this.scanResult?.kind === 'order')
        );
    }

    onSearchChanged(event: { value?: string }): void {
        this.searchCode = event.value ?? '';
        this.rebuild();
        // A new code is being typed or scanned: the open result is about the
        // previous one. Not while that result is being saved — the save's
        // completion closes it, or keeps it open to retry if the save fails.
        // A found parcel stays until the operator answers it.
        if (
            this.scanPopupVisible &&
            !this.receiptSaving &&
            normalizeCode(this.searchCode) !== this.scanCode
        ) {
            if (this.awaitingDecision) {
                this.nudgeDecision();
            } else {
                this.scanPopupVisible = false;
            }
        }
    }

    // A found parcel waits for the operator's answer — ยืนยันได้รับแล้ว or
    // ยกเลิก — and nothing else closes it: not Escape, a click outside, the
    // close button or the next scan. Closing it by habit left parcels
    // unconfirmed with nobody noticing.
    get awaitingDecision(): boolean {
        const result = this.scanResult;
        return (
            this.scanPopupVisible &&
            (result?.kind === 'match' ||
                (result?.kind === 'multi' &&
                    result.rows.some((row) => !row.received)))
        );
    }

    // ยกเลิก on a found parcel: a deliberate "not this one" — nothing is
    // recorded. A scan made meanwhile is answered next.
    cancelScan(): void {
        this.scanPopupVisible = false;
        this.answerQueuedScan();
    }

    private nudgeDecision(): void {
        this.decisionNudge = true;
        this.cdref.markForCheck();
    }

    // Enter after a scan (the scanner sends one itself): show what the code
    // found and wait for the operator. A single match is offered to confirm,
    // several are listed, an already-confirmed one is said so, and a miss
    // is checked against the cancelled orders inside the popup.
    onSearchEnter(): void {
        if (!this.hasSearch) {
            return;
        }
        const code = normalizeCode(this.searchCode);
        // A scan finished while the previous parcel was still saving: answer
        // it once the save settles, rather than replacing the popup mid-save.
        // An Enter on the code being saved is just impatience — dropped.
        if (this.receiptSaving) {
            if (code !== this.savingCode) {
                this.queuedEnter = true;
            }
            return;
        }
        // The same code again, with its result open: the operator's Enter on
        // that result — don't restart a lookup.
        if (this.scanPopupVisible && code === this.scanCode) {
            this.onOpenResultEnter();
            return;
        }
        // The next parcel scanned while a found one is still undecided: it
        // waits, and the popup asks for the answer first.
        if (this.awaitingDecision) {
            this.queuedEnter = true;
            this.nudgeDecision();
            return;
        }
        this.scanCode = code;
        this.scanShownCode = this.searchCode.trim();
        this.evaluateScan();
    }

    // Enter on an open result takes its default action: confirm a single
    // match, acknowledge a cancelled parcel or one already confirmed. Never
    // within SCAN_ENTER_GUARD_MS of the result appearing — the scanner ends
    // every code with Enter and a CR+LF scanner sends two, and that second
    // one must not confirm a parcel nobody has looked at. Several matches
    // and misses have no default: a click picks the row or records it.
    private onOpenResultEnter(): void {
        if (Date.now() - this.scanShownAt < SCAN_ENTER_GUARD_MS) {
            return;
        }
        const result = this.scanResult;
        if (result?.kind === 'match') {
            this.confirmReceived(result.row);
        } else if (
            result?.kind === 'cancelled' ||
            result?.kind === 'received'
        ) {
            this.scanPopupVisible = false;
        }
    }

    // Escape closes the scan result, else the detail popup — focus stays in
    // the scan box, so neither popup's own Escape handling sees the key.
    onSearchKeyDown(event: { event?: KeyboardEvent }): void {
        if (event.event?.key !== 'Escape') {
            return;
        }
        if (this.awaitingDecision) {
            this.nudgeDecision();
        } else if (this.scanPopupVisible) {
            this.scanPopupVisible = false;
        } else {
            this.detailPopup.close();
        }
    }

    // What the popup should say for the code in the box. While stores are
    // still loading, a miss is not yet a miss: the popup waits (see
    // refreshOpenScan) instead of running the cancelled-order check.
    private evaluateScan(): void {
        if (this.awaitingLoad) {
            this.showScanResult({ kind: 'pending', stage: 'loading' });
            return;
        }
        // A code that equals some row's order, return or tracking number
        // means that row; containment matches only count when nothing is
        // exact, so a partial hit from one store cannot stand in for the
        // exact one from another.
        const exact = this.matches.filter((row) =>
            returnSearchKeys(row).includes(this.scanCode),
        );
        const candidates = exact.length > 0 ? exact : this.matches;
        if (candidates.length === 1) {
            const row = candidates[0];
            this.showScanResult(
                row.received
                    ? { kind: 'received', row }
                    : { kind: 'match', row },
            );
        } else if (candidates.length > 1) {
            this.showScanResult({ kind: 'multi', rows: candidates });
        } else if (
            this.fromIndex &&
            this.refreshedForCode !== this.scanCode &&
            (this.refreshing ||
                Date.now() - this.lastRefreshAt > MISS_REFRESH_MIN_MS)
        ) {
            // Not in the table: it may have turned up since the table was
            // read — above all a failed delivery, which TikTok lists only
            // once it is back at the shop. Refresh once (or wait for the
            // refresh already running), then judge again. Only from the
            // index: a live-swept table is as fresh as a refresh would be,
            // and re-sweeping every store would hold the popup for long.
            const key = this.scanCode;
            this.refreshedForCode = key;
            this.showScanResult({ kind: 'pending', stage: 'refresh' });
            this.refresh(() => {
                // Only the result that is still waiting on this refresh: not
                // one the operator moved past, nor one being saved.
                if (
                    this.scanPopupVisible &&
                    this.scanCode === key &&
                    !this.receiptSaving &&
                    this.scanResult?.kind === 'pending' &&
                    this.scanResult.stage === 'refresh'
                ) {
                    this.evaluateScan();
                }
            });
        } else {
            this.showScanResult({ kind: 'pending', stage: 'cancelled' });
            this.checkCancelled();
        }
    }

    // Shows a result with the scanned code selected in the box, which keeps
    // focus: the next scan types over it, which retires this result (see
    // onSearchChanged).
    private showScanResult(result: ScanResult): void {
        this.detailPopup.close();
        this.setScanResult(result);
        this.scanPopupVisible = true;
        this.searchInput()?.select();
        this.cdref.markForCheck();
    }

    // Sets the result, restarting the Enter guard only when it is a new
    // answer: refreshOpenScan re-judges the same match on every store that
    // arrives, and that must not keep swallowing the operator's Enter.
    private setScanResult(result: ScanResult): void {
        // The scanned code is part of the answer: a new scan that happens to
        // land on the same row is a new result, and restarts the guard.
        const key = `${this.scanCode}|${
            result.kind === 'match' || result.kind === 'received'
                ? `${result.kind}:${result.row.id}`
                : result.kind
        }`;
        if (!this.scanPopupVisible || key !== this.scanShownKey) {
            this.scanShownAt = Date.now();
            this.scanShownKey = key;
            this.decisionNudge = false;
        }
        this.scanResult = result;
    }

    // Back to the box for the next scan, whichever way the popup closed, with
    // the last code selected: the grid keeps showing its result, and the
    // next scan simply types over it.
    onScanPopupHidden(): void {
        // Re-showing a popup that is still animating closed fires this
        // close's onHidden synchronously — after the new result was set.
        // Only a popup that stayed closed drops its result.
        if (!this.scanPopupVisible) {
            this.scanResult = null;
        }
        this.refocusUnlessTyping();
    }

    // Re-judges the open result when stores or receipts arrive: a scan made
    // mid-load may now have its exact match, more rows for a list, or a
    // receipt that turns 'match' into 'received'; and once every store is
    // in, a still-empty wait becomes a real miss. Results the operator is
    // acting on (a save in flight) or that came from the cancelled-order
    // check are left alone.
    private refreshOpenScan(): void {
        if (
            !this.scanPopupVisible ||
            this.receiptSaving ||
            this.scanCode !== normalizeCode(this.searchCode)
        ) {
            return;
        }
        const kind = this.scanResult?.kind;
        const waitingForLoad =
            this.scanResult?.kind === 'pending' &&
            this.scanResult.stage === 'loading';
        if (
            (waitingForLoad && (this.matches.length > 0 || !this.loading)) ||
            kind === 'match' ||
            kind === 'multi' ||
            kind === 'received'
        ) {
            this.evaluateScan();
        }
    }

    // No listed return matched: is it a cancelled order's parcel? The first
    // check builds the cancelled-orders index (a few seconds); later ones
    // answer from it. The outcome lands in the popup only if it is still
    // showing this code.
    private checkCancelled(): void {
        const code = this.searchCode.trim();
        const key = this.scanCode;
        const stillCurrent = () =>
            this.scanPopupVisible && this.scanCode === key;
        this.cancelledOrderLookup
            .find(code)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: ({ match, otherStatus, failed }) => {
                    if (!stillCurrent()) {
                        return;
                    }
                    this.setScanResult(
                        match
                            ? { kind: 'cancelled', order: match }
                            : otherStatus
                              ? { kind: 'order', order: otherStatus, failed }
                              : { kind: 'none', failed },
                    );
                    this.cdref.markForCheck();
                },
                error: (error: unknown) => {
                    if (!stillCurrent()) {
                        return;
                    }
                    this.scanPopupVisible = false;
                    this.notificationService.pushMessage({
                        message: `ตรวจสอบคำสั่งซื้อที่ยกเลิกไม่ได้: ${this.utilService.errorToString(error)}`,
                        messageType: MessageType.error,
                    });
                    this.cdref.markForCheck();
                },
            });
    }

    onFilterChanged(): void {
        this.rebuild();
    }

    confirmReceived(row: ReturnRow): void {
        if (!this.canMark()) {
            return;
        }
        this.receiptSaving = true;
        this.savingCode = this.scanPopupVisible ? this.scanCode : '';
        // From the popup, the code it was opened for — the box may already
        // hold the next parcel's. From a grid row, what the box shows.
        const code = this.scanPopupVisible
            ? this.scanShownCode
            : this.searchCode.trim();
        // The click moved focus to the button; put it back so a scan made
        // during the save lands in the box.
        this.focusSearch();
        this.returnReceiptService
            .mark(row.id, code || undefined)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (doc) => {
                    this.receipts = doc;
                    this.receiptSaving = false;
                    this.notificationService.pushMessage({
                        message: `รับแล้ว: ${row.storeTitle} · คำสั่งซื้อ ${row.orderNo}${row.trackingNumber ? ` · ${row.trackingNumber}` : ''}`,
                        messageType: MessageType.success,
                    });
                    // Ready for the next scan: the code stays in the box
                    // (selected, by onScanPopupHidden) so the grid keeps
                    // showing the row just confirmed, now green.
                    this.rebuild();
                    this.closeScanPopupOrRefocus();
                    this.answerQueuedScan();
                },
                error: (error: unknown) => this.markFailed(error),
            });
    }

    // The package is on the counter but nothing listed matches its code —
    // it arrived ahead of the platform's record, or outside the lookback.
    // Recorded against the code so it attaches to the return once listed.
    recordUnmatched(): void {
        if (!this.canMark() || !this.canRecordUnmatched) {
            return;
        }
        const code = this.searchCode.trim();
        this.receiptSaving = true;
        this.savingCode = this.scanCode;
        this.focusSearch();
        this.returnReceiptService
            .mark(codeReceiptKey(code), code)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (doc) => {
                    this.receipts = doc;
                    this.receiptSaving = false;
                    this.notificationService.pushMessage({
                        message: `บันทึก "${code}" ว่ารับแล้ว จะไปแสดงที่รายการคืนเมื่อแพลตฟอร์มส่งข้อมูลมา`,
                        messageType: MessageType.success,
                    });
                    this.rebuild();
                    this.closeScanPopupOrRefocus();
                    this.answerQueuedScan();
                },
                error: (error: unknown) => this.markFailed(error),
            });
    }

    // A scan whose Enter arrived during a save or an undecided found parcel
    // (see onSearchEnter).
    private answerQueuedScan(): void {
        if (this.queuedEnter) {
            this.queuedEnter = false;
            this.onSearchEnter();
        }
    }

    async unmarkReceived(row: ReturnRow): Promise<void> {
        if (!this.canMark()) {
            return;
        }
        const ok = await confirm(
            `ยกเลิกการยืนยันรับของ ${row.storeTitle} คำสั่งซื้อ ${row.orderNo}?`,
            'ยกเลิกการยืนยันรับ',
        );
        if (!ok) {
            return;
        }
        this.receiptSaving = true;
        this.savingCode = '';
        this.returnReceiptService
            .unmark([
                row.id,
                ...returnSearchKeys(row).map((key) => `code:${key}`),
            ])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (doc) => {
                    this.receipts = doc;
                    this.receiptSaving = false;
                    this.rebuild();
                    // A scan made during the unmark gets its answer too.
                    this.answerQueuedScan();
                },
                error: (error: unknown) => this.markFailed(error),
            });
    }

    // Received rows fade; a delivered package nobody has confirmed is the
    // one to go and look for, so its stage cell is flagged.
    onRowPrepared(event: any): void {
        if (event.rowType !== 'data' || !event.data) {
            return;
        }
        const row: ReturnRow = event.data;
        if (row.received) {
            event.rowElement.classList.add('received-row');
        } else if (row.packageStage === 'delivered') {
            event.rowElement.classList.add('awaiting-confirmation-row');
        }
    }

    private canMark(): boolean {
        if (!this.receiptsLoaded) {
            this.notificationService.pushMessage({
                message: 'ยังโหลดข้อมูลการรับไม่ได้ — โหลดใหม่ก่อนบันทึก',
                messageType: MessageType.warning,
            });
            return false;
        }
        return !this.receiptSaving;
    }

    private markFailed(error: unknown): void {
        this.receiptSaving = false;
        this.answerQueuedScan();
        this.notificationService.pushMessage({
            message: `บันทึกไม่ได้: ${this.utilService.errorToString(error)}`,
            messageType: MessageType.error,
        });
        this.cdref.markForCheck();
    }

    private reportFetchProblem(report: ReturnFetchReport): void {
        if (report.error) {
            this.notificationService.pushMessage({
                message: `${report.title}: ${report.error}`,
                messageType: MessageType.error,
            });
        }
        if (report.failedDeliveryError) {
            this.notificationService.pushMessage({
                message: `${report.title}: โหลดพัสดุตีกลับไม่ได้ — ${report.failedDeliveryError}`,
                messageType: MessageType.warning,
            });
        }
        if (report.detailFailures) {
            this.notificationService.pushMessage({
                message: `${report.title}: ${report.detailFailures} รายการแสดงโดยไม่มีสถานะขนส่ง — สถานะพัสดุอาจต่ำกว่าจริง`,
                messageType: MessageType.warning,
            });
        }
    }

    private rebuild(): void {
        this.rows = this.fetched.filter(
            (row) =>
                PACKAGE_EXPECTED_STAGES.includes(row.packageStage) ||
                !!receiptFor(row, this.receipts),
        );
        const all = this.rows.map((row) => this.toRow(row));
        this.matches = this.hasSearch
            ? all.filter((row) => matchesCode(row, this.searchCode))
            : [];
        this.dataSource = this.hasSearch
            ? this.matches
            : all.filter((row) => !this.hideReceived || !row.received);
        this.cdref.markForCheck();
    }

    private toRow(row: ReturnPackage): ReturnRow {
        const receipt = receiptFor(row, this.receipts);
        return {
            ...row,
            sourceLabel: PACKAGE_SOURCE_LABELS[row.source],
            stageLabel: receipt
                ? RECEIVED_STAGE_LABEL
                : PACKAGE_STAGE_LABELS[row.packageStage],
            platformStageLabel: PACKAGE_STAGE_LABELS[row.packageStage],
            itemsSummary: row.items
                .map((item) =>
                    item.qty > 1 ? `${item.sku} ×${item.qty}` : item.sku,
                )
                .join(', '),
            createdAtDate: row.createdAt ? row.createdAt.toDate() : null,
            received: !!receipt,
            receivedAt: receipt?.at ?? null,
            receivedBy: receipt?.by ?? '',
        };
    }

    // After a save from the popup or a grid row: close the popup (its hidden
    // handler refocuses), or — a save from a grid row, with no popup —
    // refocus directly.
    private closeScanPopupOrRefocus(): void {
        if (this.scanPopupVisible) {
            this.scanPopupVisible = false;
            return;
        }
        this.refocusUnlessTyping();
    }

    // Back to the scan box with its text selected — unless focus is already
    // there, which means a new code is being typed or scanned and must not
    // be selected over mid-stream.
    private refocusUnlessTyping(): void {
        if (document.activeElement !== this.searchInput()) {
            this.focusSearch();
        }
    }

    // Focus the scan box and select what is in it, so the next scan (or
    // keystroke) replaces the previous code instead of appending to it.
    private focusSearch(): void {
        setTimeout(() => {
            this.searchBox?.instance?.focus();
            this.searchInput()?.select();
        });
    }

    private searchInput(): HTMLInputElement | null {
        const host = this.searchBox?.instance?.element() as
            HTMLElement | undefined;
        return host?.querySelector('input') ?? null;
    }
}
